"use client"

import React, { useRef, useState, useCallback, useEffect } from "react"
import { motion, useSpring, useTransform, SpringOptions } from "framer-motion"

type SpotlightProps = {
  className?: string
  size?: number
  springOptions?: SpringOptions
  children?: React.ReactNode
}

export function Spotlight({
  className = "",
  size = 400,
  springOptions = { bounce: 0 },
  children,
}: SpotlightProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [parentElement, setParentElement] = useState<HTMLElement | null>(null)

  const mouseX = useSpring(0, springOptions)
  const mouseY = useSpring(0, springOptions)

  const spotX = useTransform(mouseX, (x) => `${x - size / 2}px`)
  const spotY = useTransform(mouseY, (y) => `${y - size / 2}px`)

  useEffect(() => {
    if (containerRef.current) {
      const parent = containerRef.current.parentElement
      if (parent) {
        parent.style.position = "relative"
        parent.style.overflow = "hidden"
        setParentElement(parent)
      }
    }
  }, [])

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!parentElement) return
      const { left, top } = parentElement.getBoundingClientRect()
      mouseX.set(event.clientX - left)
      mouseY.set(event.clientY - top)
    },
    [mouseX, mouseY, parentElement]
  )

  useEffect(() => {
    if (!parentElement) return

    const onMouseEnter = () => setIsHovered(true)
    const onMouseLeave = () => setIsHovered(false)

    parentElement.addEventListener("mousemove", handleMouseMove)
    parentElement.addEventListener("mouseenter", onMouseEnter)
    parentElement.addEventListener("mouseleave", onMouseLeave)

    return () => {
      parentElement.removeEventListener("mousemove", handleMouseMove)
      parentElement.removeEventListener("mouseenter", onMouseEnter)
      parentElement.removeEventListener("mouseleave", onMouseLeave)
    }
  }, [parentElement, handleMouseMove])

  return (
    <motion.div
      ref={containerRef}
      className={`pointer-events-none absolute rounded-full bg-[radial-gradient(circle_at_center,var(--spotlight-color,rgba(197,155,86,0.18)),transparent_70%)] blur-2xl transition-opacity duration-300 ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        left: spotX,
        top: spotY,
        opacity: isHovered ? 1 : 0,
      }}
    >
      {children}
    </motion.div>
  )
}
